import {
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_PLAN,
  SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL,
} from 'constants/uiConstants';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import type { SummaryDataBasis } from '../../../../../redux/slices/InsightsSlice';
import { ACTUAL_ONLY_CATEGORIES, PLAN_ONLY_CATEGORIES } from '../insightUtils';

/**
 * One column group in a basis-aware Summary section — Plan or Actual. A
 * section never sums the two: Plan vs Actual renders both sides as paired
 * columns instead (OP-2387).
 */
export type SummaryDataSide =
  | typeof SUMMARY_DATA_BASIS_PLAN
  | typeof SUMMARY_DATA_BASIS_ACTUAL;

/** The task categories each side reads — exactly one category per side. */
export const SUMMARY_DATA_SIDE_CATEGORIES: Record<
  SummaryDataSide,
  Set<InputTaskCategory>
> = {
  [SUMMARY_DATA_BASIS_PLAN]: PLAN_ONLY_CATEGORIES,
  [SUMMARY_DATA_BASIS_ACTUAL]: ACTUAL_ONLY_CATEGORIES,
};

/** Sides a data basis renders, in column order. */
export const resolveDataSides = (basis: SummaryDataBasis): SummaryDataSide[] =>
  basis === SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL
    ? [SUMMARY_DATA_BASIS_PLAN, SUMMARY_DATA_BASIS_ACTUAL]
    : [basis];

/** A row matched across sides by key — a side is absent when it has no data for that row. */
export interface SideBySideRow<T> {
  key: string;
  sides: Partial<Record<SummaryDataSide, T>>;
}

/**
 * Match each side's rows by key, keeping first-seen order (Plan's order
 * first, then any Actual-only rows). Callers re-sort if their section has a
 * display order of its own.
 */
export const pairRowsBySide = <T>(
  rowsBySide: Partial<Record<SummaryDataSide, T[]>>,
  keyOf: (row: T) => string
): SideBySideRow<T>[] => {
  const paired = new Map<string, SideBySideRow<T>>();
  for (const side of [
    SUMMARY_DATA_BASIS_PLAN,
    SUMMARY_DATA_BASIS_ACTUAL,
  ] as const) {
    for (const row of rowsBySide[side] ?? []) {
      const key = keyOf(row);
      const existing = paired.get(key) ?? { key, sides: {} };
      existing.sides[side] = row;
      paired.set(key, existing);
    }
  }
  return Array.from(paired.values());
};
